import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useInterval } from "@/lib/useInterval";
import { Moon } from "lucide-react";

/**
 * Session 46 (2026-06-07) — Weekend / After-hours feed banner.
 *
 * Polls /api/market-status every 30s (visibility-aware). When NEITHER NSEFO
 * NOR MCX is currently OPEN, shows a sticky amber banner explaining that
 * Market Watch is frozen at the last legitimate session-close snapshot.
 *
 * If admin has set ALLOW_AFTER_HOURS_FEED=true, the banner is suppressed
 * (operator has explicitly opted in to seeing weekend ticks).
 *
 * No re-renders on tick stream — only on the 30s poll.
 */
export default function MarketClosedBanner() {
  const [status, setStatus] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/market-status");
      setStatus(r.data || null);
    } catch (_) {
      // Silent — banner is optional UX, not a critical request
    }
  };

  useEffect(() => { load(); }, []);
  useInterval(load, 30000, { runOnMount: false });

  if (!status) return null;
  // Admin opted into after-hours feed → don't show banner.
  if (status.allow_after_hours_feed) return null;
  // Any exchange OPEN → no banner needed.
  if (status.any_open) return null;

  // Build the per-exchange label
  const ex = status.exchanges || {};
  const parts = ["NSEFO", "MCX"].map((k) => {
    const e = ex[k];
    if (!e) return null;
    const lbl = e.label ? ` (${e.label})` : "";
    return `${k}: ${e.status}${lbl}`;
  }).filter(Boolean).join(" · ");

  return (
    <div
      className="sticky top-0 z-50 bg-amber-100 text-amber-900 border-b border-amber-300 px-4 py-2 text-xs flex items-center justify-center gap-2 shadow-sm"
      data-testid="market-closed-banner"
    >
      <Moon className="h-3.5 w-3.5" />
      <span className="font-semibold">Market Closed</span>
      <span className="opacity-80">— Showing Last Official Close Prices</span>
      <span className="hidden md:inline opacity-60 ml-2 font-mono text-[10px]">
        ({parts})
      </span>
    </div>
  );
}
